import React, { useState, useEffect } from 'react';
import {
  Package,
  Box,
  TrendingDown,
  TrendingUp,
  AlertTriangle,
  Search,
  Filter,
  Plus,
  ChevronRight,
  ArrowDownCircle,
  ArrowUpCircle,
  Truck,
  BarChart3,
  RefreshCw,
  MapPin
} from 'lucide-react';

// Componente de tarjeta de producto
const ProductCard = ({ product }) => {
  const stockStatus = product.stock <= product.stockMinimo ? 'bajo' :
                      product.stock <= product.stockMinimo * 2 ? 'medio' : 'alto';

  const statusColors = {
    bajo: 'bg-red-50 border-red-200 text-red-700',
    medio: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    alto: 'bg-green-50 border-green-200 text-green-700'
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 hover:border-gray-200 transition-colors">
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center">
          <Package className="w-8 h-8 text-[#6B7280]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-medium text-[#111827]">{product.nombre}</h4>
              <p className="text-sm text-[#6B7280]">{product.categoria}</p>
            </div>
            <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${statusColors[stockStatus]}`}>
              {stockStatus === 'bajo' ? 'Stock bajo' : stockStatus === 'medio' ? 'Stock medio' : 'En stock'}
            </span>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-[#6B7280]">SKU: <span className="text-[#111827] font-medium">{product.sku}</span></span>
              <span className="text-[#6B7280]">Stock: <span className="text-[#111827] font-medium">{product.stock}</span></span>
            </div>
            <p className="font-semibold text-[#111827]">{product.precio}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente de movimiento de stock
const MovementItem = ({ movement }) => {
  const isEntry = movement.tipo === 'entrada';

  return (
    <div className="flex items-center gap-4 p-4 hover:bg-gray-50 rounded-xl transition-colors">
      <div className={`p-2 rounded-lg ${isEntry ? 'bg-green-50' : 'bg-red-50'}`}>
        {isEntry ? (
          <ArrowDownCircle className="w-5 h-5 text-green-600" />
        ) : (
          <ArrowUpCircle className="w-5 h-5 text-red-600" />
        )}
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-[#111827]">{movement.producto}</p>
        <p className="text-xs text-[#6B7280]">{movement.motivo}</p>
      </div>
      <div className="text-right">
        <p className={`text-sm font-medium ${isEntry ? 'text-green-600' : 'text-red-600'}`}>
          {isEntry ? '+' : '-'}{movement.cantidad} uds
        </p>
        <p className="text-xs text-[#6B7280]">{movement.fecha}</p>
      </div>
    </div>
  );
};

export default function AlmacenPanel() {
  const [activeTab, setActiveTab] = useState('inventario');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const [kpis] = useState({
    totalProductos: { value: 234, variacion: '+12' },
    valorStock: { value: '€45.680', variacion: '+8%' },
    stockBajo: { value: 8, criticos: 3 },
    movimientosMes: { value: 156, tendencia: '+23%' }
  });

  const [products] = useState([
    { nombre: 'TPV Tactil 15"', sku: 'TPV-001', categoria: 'Hardware', stock: 12, stockMinimo: 5, precio: '€850' },
    { nombre: 'Impresora Tickets 80mm', sku: 'IMP-002', categoria: 'Perifericos', stock: 3, stockMinimo: 5, precio: '€145' },
    { nombre: 'Cajon Portamonedas', sku: 'CAJ-003', categoria: 'Perifericos', stock: 8, stockMinimo: 3, precio: '€75' },
    { nombre: 'Lector Codigo Barras', sku: 'LEC-004', categoria: 'Perifericos', stock: 2, stockMinimo: 5, precio: '€95' },
    { nombre: 'Cable Red Cat6 3m', sku: 'CAB-005', categoria: 'Cableado', stock: 45, stockMinimo: 20, precio: '€8' },
    { nombre: 'Router WiFi Empresarial', sku: 'ROU-006', categoria: 'Redes', stock: 6, stockMinimo: 3, precio: '€320' },
    { nombre: 'Disco SSD 256GB', sku: 'SSD-007', categoria: 'Almacenamiento', stock: 15, stockMinimo: 10, precio: '€65' },
    { nombre: 'Monitor 22" Full HD', sku: 'MON-008', categoria: 'Hardware', stock: 4, stockMinimo: 5, precio: '€195' }
  ]);

  const [movements] = useState([
    { tipo: 'entrada', producto: 'TPV Tactil 15"', cantidad: 10, motivo: 'Pedido proveedor #2456', fecha: 'Hoy 10:30' },
    { tipo: 'salida', producto: 'Impresora Tickets 80mm', cantidad: 2, motivo: 'Instalacion Hotel Mar', fecha: 'Hoy 09:15' },
    { tipo: 'salida', producto: 'Cajon Portamonedas', cantidad: 1, motivo: 'Instalacion Bar Central', fecha: 'Ayer 16:45' },
    { tipo: 'entrada', producto: 'Cable Red Cat6 3m', cantidad: 50, motivo: 'Reposicion almacen', fecha: 'Ayer 11:00' },
    { tipo: 'salida', producto: 'Router WiFi', cantidad: 1, motivo: 'Proyecto Franquicia', fecha: '14 Ene' }
  ]);

  const [suppliers] = useState([
    { nombre: 'TechDistrib S.L.', productos: 45, ultimoPedido: '10 Ene', estado: 'activo' },
    { nombre: 'Hardware Plus', productos: 32, ultimoPedido: '8 Ene', estado: 'activo' },
    { nombre: 'Redes y Cables', productos: 18, ultimoPedido: '5 Ene', estado: 'activo' },
    { nombre: 'TPV Solutions', productos: 12, ultimoPedido: '28 Dic', estado: 'inactivo' }
  ]);

  const tabs = [
    { id: 'inventario', label: 'Inventario', icon: Package },
    { id: 'movimientos', label: 'Movimientos', icon: RefreshCw },
    { id: 'proveedores', label: 'Proveedores', icon: Truck },
    { id: 'ubicaciones', label: 'Ubicaciones', icon: MapPin }
  ];

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#111827]">Almacen</h1>
          <p className="text-[#6B7280] mt-1">Stock e inventario</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-[#6B7280] hover:bg-gray-50">
            <ArrowDownCircle className="w-4 h-4" />
            <span>Entrada</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-[#6B7280] hover:bg-gray-50">
            <ArrowUpCircle className="w-4 h-4" />
            <span>Salida</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors">
            <Plus className="w-4 h-4" />
            <span>Nuevo producto</span>
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#6B7280]">Total productos</p>
              <p className="text-2xl font-semibold text-[#111827] mt-1">{kpis.totalProductos.value}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl">
              <Box className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <p className="text-xs text-green-600 mt-2">{kpis.totalProductos.variacion} este mes</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#6B7280]">Valor stock</p>
              <p className="text-2xl font-semibold text-[#111827] mt-1">{kpis.valorStock.value}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-xl">
              <BarChart3 className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="text-xs text-green-600 mt-2">{kpis.valorStock.variacion} vs mes anterior</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#6B7280]">Stock bajo</p>
              <p className="text-2xl font-semibold text-[#111827] mt-1">{kpis.stockBajo.value}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
          </div>
          <p className="text-xs text-red-600 mt-2">{kpis.stockBajo.criticos} criticos</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#6B7280]">Movimientos mes</p>
              <p className="text-2xl font-semibold text-[#111827] mt-1">{kpis.movimientosMes.value}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-xl">
              <RefreshCw className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <p className="text-xs text-green-600 mt-2">{kpis.movimientosMes.tendencia} actividad</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-[#111827] shadow-sm'
                  : 'text-[#6B7280] hover:text-[#111827]'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <input
              type="text"
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3B82F6] w-64"
            />
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-[#6B7280] hover:bg-gray-50">
            <Filter className="w-4 h-4" />
            Filtros
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'inventario' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            {products.map((product, index) => (
              <ProductCard key={index} product={product} />
            ))}
          </div>

          {/* Sidebar - Alertas de stock */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <h3 className="font-semibold text-[#111827]">Alertas de stock</h3>
              </div>
              <div className="space-y-3">
                {products.filter(p => p.stock <= p.stockMinimo).map((product, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
                    <div>
                      <p className="text-sm font-medium text-[#111827]">{product.nombre}</p>
                      <p className="text-xs text-[#6B7280]">Min: {product.stockMinimo} uds</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-red-600">{product.stock} uds</p>
                      <button className="text-xs text-[#3B82F6] hover:underline">Pedir</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h3 className="font-semibold text-[#111827] mb-4">Ultimos movimientos</h3>
              <div className="space-y-2">
                {movements.slice(0, 3).map((movement, index) => (
                  <div key={index} className="flex items-center gap-3 text-sm">
                    {movement.tipo === 'entrada' ? (
                      <ArrowDownCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <ArrowUpCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span className="flex-1 truncate text-[#111827]">{movement.producto}</span>
                    <span className={movement.tipo === 'entrada' ? 'text-green-600' : 'text-red-600'}>
                      {movement.tipo === 'entrada' ? '+' : '-'}{movement.cantidad}
                    </span>
                  </div>
                ))}
              </div>
              <button className="w-full mt-3 text-sm text-[#3B82F6] hover:underline">
                Ver todos los movimientos
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'movimientos' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-[#111827]">Historial de movimientos</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {movements.map((movement, index) => (
              <MovementItem key={index} movement={movement} />
            ))}
          </div>
        </div>
      )}

      {activeTab === 'proveedores' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-6 py-4 text-sm font-medium text-[#6B7280]">Proveedor</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[#6B7280]">Productos</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[#6B7280]">Ultimo pedido</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[#6B7280]">Estado</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((supplier, index) => (
                <tr key={index} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#3B82F6] bg-opacity-10 rounded-lg flex items-center justify-center">
                        <Truck className="w-5 h-5 text-[#3B82F6]" />
                      </div>
                      <span className="font-medium text-[#111827]">{supplier.nombre}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#6B7280]">{supplier.productos} productos</td>
                  <td className="px-6 py-4 text-sm text-[#6B7280]">{supplier.ultimoPedido}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                      supplier.estado === 'activo' ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-600'
                    }`}>
                      {supplier.estado}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button className="p-1 hover:bg-gray-100 rounded-lg">
                      <ChevronRight className="w-4 h-4 text-[#6B7280]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'ubicaciones' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { nombre: 'Almacen Principal', direccion: 'Nave 1, Pol. Industrial', productos: 180, valor: '€38.500' },
            { nombre: 'Almacen Secundario', direccion: 'Nave 3, Pol. Industrial', productos: 42, valor: '€5.200' },
            { nombre: 'Oficina Central', direccion: 'C/ Mayor 15', productos: 12, valor: '€1.980' }
          ].map((location, index) => (
            <div key={index} className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#111827]">{location.nombre}</h3>
                  <p className="text-xs text-[#6B7280]">{location.direccion}</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B7280]">Productos</span>
                  <span className="font-medium text-[#111827]">{location.productos}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B7280]">Valor stock</span>
                  <span className="font-medium text-[#111827]">{location.valor}</span>
                </div>
              </div>
              <button className="w-full mt-4 py-2 text-sm text-[#3B82F6] hover:bg-blue-50 rounded-lg transition-colors">
                Ver inventario
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
